import { useState } from 'react';
import { VStack, Box, Image, Stack, AspectRatio } from '@chakra-ui/react';
import {
  AddToCartBottomBar,
  FixedBackwardBar,
  ItemModifier,
  ItemInfo,
  NoteInput,
  InventoryNoticeModal,
} from './components';
import PageContainer from '~/container/PageContainer';
import useScroll from '~/hooks/useScroll';

import { useParams, useNavigate, Navigate } from 'react-router-dom';

import { itemSelector } from '~/recoil/selectors/items';
import cartItemSelector from '~/recoil/selectors/cartItem';
import { useRecoilValue } from 'recoil';

import useAllowOperateCart from '~/pages/Item/hooks/useAllowOperateCart';
import useModifiersControl from '~/pages/Item/hooks/useModifiersControl';
import useRemainInventory from '~/pages/Item/hooks/useRemainInventory';
import useItemModifiers from '~/pages/Item/hooks/useItemModifiers';
import useCartItems from '~/hooks/useCartItems';
import { CartItem, OrderModifier } from '~/types';

import ImageSrcError from '~/assets/images/ImageSrcError.png';

import { useTranslation } from 'react-i18next';

export default function ItemEdit() {
  const { t } = useTranslation();
  const { storeId, itemId = '', index = '' } = useParams();
  const navigate = useNavigate();
  const { positionInfo } = useScroll();

  const [showError, setShowError] = useState<boolean>(false);
  const [inventoryErrorModal, setInventoryErrorModal] = useState<'none' | 'item' | 'option'>(
    'none',
  );

  const item = useRecoilValue(itemSelector(itemId));
  const cartItem = useRecoilValue(cartItemSelector({ itemId, index: parseInt(index) }));

  const { cartItems, handleEditItem } = useCartItems();
  // check operating time
  const allowAddOperateCart = useAllowOperateCart(itemId);

  // 品項庫存剩餘量 remainInventory 剩餘庫存 , targetItemCount 購物車總共選了多少個
  // 因為正在編輯該品項所以不應該把它算進去購物車總量裡面，要加回來 remainInventory - targetItemCount + "cartItem.count"
  const { remainInventory, targetItemCount } = useRemainInventory(
    item?.inventory,
    itemId,
    cartItems,
  );

  // 選項庫存剩餘量
  const { modifiersWithRemainCount, handleCheckModifierOptionError } = useItemModifiers(
    item?.modifiers || [],
    {
      cartItemModifiers: cartItem?.modifiers,
      compensativeCount: cartItem?.count,
    },
  );

  // control item info hook
  const {
    selectedModifiers,
    modifierErrors,
    optionsTotalPrice,
    count,
    note,
    handleChangeCount,
    handleChangeModifier,
    handleChangeNote,
  } = useModifiersControl(item?.modifiers || null, {
    defaultModifiers: cartItem?.modifiers,
    defaultCount: cartItem?.count,
    defaultNote: cartItem?.note,
  });

  if (!item || !cartItem) {
    return <Navigate to={`/${storeId}`} />;
  }

  const handleClickEditItem = () => {
    if (modifierErrors.size > 0) {
      setShowError(true);
      return;
    }

    if (count > remainInventory - targetItemCount + cartItem.count) {
      setInventoryErrorModal('item');
      console.log('edit item inventory error');
      return;
    }

    const modifiers: Array<OrderModifier> = [...selectedModifiers.values()];

    if (handleCheckModifierOptionError(modifiers, count)) {
      console.log('edit item option inventory error');
      setInventoryErrorModal('option');
      return;
    }

    const editCartItem: CartItem = {
      id: itemId,
      name: item.name,
      price: item.price,
      tax: item.tax,
      modifiers,
      count,
      note,
    };

    handleEditItem(editCartItem, parseInt(index));
    navigate(`/${storeId}/cart`);
  };

  const displayInventory =
    remainInventory - targetItemCount + cartItem.count <= 0
      ? 0
      : remainInventory - targetItemCount + cartItem.count;

  return (
    <PageContainer hideHeader hideFooter>
      <FixedBackwardBar title={t('backCart')} onNavigate={() => navigate(`/${storeId}/cart`)} />
      <VStack align="stretch" justify="space-between" spacing={4} minH="100%" px={4}>
        <Stack direction={{ base: 'column', md: 'row' }} align="stretch" spacing={10}>
          <Box flex="1" position="relative">
            {item.image && (
              <AspectRatio
                m="auto"
                maxW="1000px"
                ratio={1}
                pos={{ base: 'static', md: 'sticky' }}
                top={20}
                zIndex="50"
              >
                <Image
                  src={item.image}
                  fallbackSrc={ImageSrcError}
                  w="full"
                  pos={{ base: 'static', md: 'sticky' }}
                  top={20}
                  zIndex="100"
                />
              </AspectRatio>
            )}
          </Box>
          <VStack align="stretch" flex="1" spacing={5}>
            <ItemInfo
              title={item.name}
              description={item.description}
              price={item.price}
              inventory={displayInventory}
            />
            <ItemModifier
              itemModifiers={modifiersWithRemainCount}
              selectorModifiers={selectedModifiers}
              modifierErrors={modifierErrors}
              showError={showError}
              itemCount={count}
              positionInfo={positionInfo}
              onChangeModifier={handleChangeModifier}
            />
            <NoteInput
              value={note}
              onChange={(e) => handleChangeNote(e.target.value)}
              textLimit={100}
            />
            {!item.image && (
              <Box h="400px" pos={{ base: 'static', md: 'sticky' }} top={20} zIndex="100" />
            )}
          </VStack>
        </Stack>
        <AddToCartBottomBar
          isDisabled={!allowAddOperateCart}
          amount={count}
          currentInventory={displayInventory}
          unitPrice={optionsTotalPrice + item.price}
          onChangeCount={handleChangeCount}
          onAddItemClick={handleClickEditItem}
        />
      </VStack>

      <InventoryNoticeModal
        isOpen={inventoryErrorModal === 'item'}
        title={t('store.item.soldOutNote')}
        message={t('item.reselectNote')}
        onFooterClick={() => {
          setInventoryErrorModal('none');
          navigate(`/${storeId}/cart`);
        }}
      />
      <InventoryNoticeModal
        isOpen={inventoryErrorModal === 'option'}
        title={t('item.option.soldOut')}
        message={t('item.option.reselectNote')}
        onFooterClick={() => setInventoryErrorModal('none')}
      />
    </PageContainer>
  );
}
